import { Fragment, useEffect, type ComponentType, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/cn";
import { HamburgerMenuIcon, SidebarMinimalisticIcon } from "../icons";

export type RailItem = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  badge?: { n: number; alarm?: boolean };
  /** Lives in another area: shown with ↗. */
  external?: boolean;
};

export type RailGroup = { label: string; items: RailItem[] };

/** The grid a rail sits in: the column animates between 232px and 68px, and the page reflows. */
export function RailGrid({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className="grid items-start gap-7 transition-[grid-template-columns] duration-[180ms] ease-out motion-reduce:transition-none"
      style={{ gridTemplateColumns: `${collapsed ? 68 : 232}px minmax(0,1fr)` }}
    >
      {children}
    </div>
  );
}

function Count({ badge, mini }: { badge?: RailItem["badge"]; mini?: boolean }) {
  if (!badge?.n) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full font-mono",
        mini
          ? "absolute right-0 top-0.5 h-[15px] min-w-4 px-1 text-[9px]"
          : "h-[18px] min-w-5 px-1.5 text-[10px]",
        badge.alarm ? "bg-rhodo text-[#FFFDF8]" : "bg-line-strong text-ink",
      )}
    >
      {badge.n}
    </span>
  );
}

/**
 * A section list down the left of a desk screen: Upkeep's and Settings'. It
 * folds to a strip of icons (the toggle at its top, or `[`), and the choice
 * is kept on this device for both.
 */
export function SectionRail({
  title,
  sub,
  groups,
  active,
  collapsed,
  onToggle,
  top,
}: {
  title: string;
  sub?: string;
  groups: RailGroup[];
  /** The `to` of the item to mark as current. */
  active: string | undefined;
  collapsed: boolean;
  onToggle: () => void;
  /** Above the groups while expanded: a property picker. */
  top?: ReactNode;
}) {
  // `[` folds and unfolds, unless someone is typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "[" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))
      )
        return;
      e.preventDefault();
      onToggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onToggle]);

  return (
    // Clears the sticky header and course bar above it.
    <nav
      aria-label={`${title} sections`}
      className="sticky top-[150px] flex max-h-[calc(100dvh-170px)] flex-col gap-3 overflow-y-auto overflow-x-hidden pb-4 print:hidden"
    >
      <div
        className={cn(
          "flex items-center gap-2",
          collapsed ? "justify-center" : "px-2.5",
        )}
      >
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-display truncate text-[20px] font-semibold leading-tight">
              {title}
            </div>
            {sub && (
              <div className="truncate text-[12.5px] text-muted">{sub}</div>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          aria-expanded={!collapsed}
          title={`${collapsed ? "Expand" : "Collapse"} menu  [`}
          className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-line-strong bg-surface text-muted hover:text-ink"
        >
          {collapsed ? (
            <HamburgerMenuIcon size={18} />
          ) : (
            <SidebarMinimalisticIcon size={18} />
          )}
        </button>
      </div>
      {!collapsed && top}
      {groups.map((g, gi) => (
        <Fragment key={g.label || gi}>
          {collapsed && gi > 0 && (
            <div aria-hidden className="mx-2 h-px bg-line" />
          )}
          <div
            className={cn("flex flex-col gap-0.5", collapsed && "items-center")}
          >
            {!collapsed && g.label && (
              <div className="px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[.14em] text-subtle">
                {g.label}
              </div>
            )}
            {g.items.map((it) => {
              const on = !it.external && it.to === active;
              const Icon = it.icon;
              const tip = it.badge?.n
                ? `${it.label} · ${it.badge.n}`
                : it.label;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  aria-current={on ? "page" : undefined}
                  aria-label={collapsed ? it.label : undefined}
                  title={collapsed ? tip : undefined}
                  className={cn(
                    "relative flex items-center rounded-sm no-underline",
                    collapsed
                      ? "size-11 justify-center"
                      : "min-h-10 gap-2.5 px-2.5 text-[14px]",
                    on
                      ? "bg-deodar-tint font-semibold text-ink"
                      : "text-muted hover:bg-sunk hover:text-ink",
                  )}
                >
                  <Icon
                    size={19}
                    className={cn(
                      "shrink-0",
                      on ? "text-deodar" : "text-subtle",
                    )}
                  />
                  {!collapsed && (
                    <>
                      <span className="min-w-0 flex-1 truncate">
                        {it.label}
                      </span>
                      {it.external && (
                        <span aria-hidden className="text-subtle">
                          ↗
                        </span>
                      )}
                      <Count badge={it.badge} />
                    </>
                  )}
                  {collapsed && !it.external && <Count badge={it.badge} mini />}
                </Link>
              );
            })}
          </div>
        </Fragment>
      ))}
    </nav>
  );
}

/** Phones: the same sections as a row of chips that scrolls sideways under the header. */
export function SectionChips({
  groups,
  active,
}: {
  groups: RailGroup[];
  active: string | undefined;
}) {
  return (
    <nav
      aria-label="Sections"
      className="no-scrollbar -mx-3.5 flex gap-1 overflow-x-auto px-3.5 print:hidden"
    >
      {groups
        .flatMap((g) => g.items)
        .map((it) => {
          const on = !it.external && it.to === active;
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              aria-current={on ? "page" : undefined}
              className={cn(
                "flex min-h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-sm px-3.5 text-[14px] no-underline",
                on
                  ? "bg-surface font-semibold text-ink shadow-[inset_0_0_0_1px_var(--border-strong)]"
                  : "text-muted",
              )}
            >
              <Icon size={18} className={on ? "text-deodar" : ""} />
              {it.label}
              {it.external && <span aria-hidden>↗</span>}
              <Count badge={it.badge} />
            </Link>
          );
        })}
    </nav>
  );
}
